import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, Shield, Heart, Lock, KeyRound } from "lucide-react";
import api from "../../api/axios";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  const validateMobile = () => {
    if (!mobile.trim()) {
      setErrors({ mobile: "Mobile number is required" });
      return false;
    }
    if (!/^\d{10}$/.test(mobile.replace(/\D/g, ""))) {
      setErrors({ mobile: "Please enter a valid 10-digit mobile number" });
      return false;
    }
    setErrors({});
    return true;
  };

  const validateResetForm = () => {
    const newErrors = {};
    
    if (!otp.trim()) {
      newErrors.otp = "OTP is required";
    } else if (otp.length !== 6) {
      newErrors.otp = "OTP must be 6 digits";
    }
    
    if (!newPassword.trim()) {
      newErrors.password = "New password is required";
    } else if (newPassword.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    
    if (!validateMobile()) return;
    
    setLoading(true);
    try {
      // Remove spaces from mobile number before sending to API
      const cleanMobile = mobile.replace(/\s/g, '');
      
      // Your API call here
      await api.post("/send-otp/", { mobile: cleanMobile, otp_type: 'forgot_password' });
      
      // Remove this simulation in production
      alert("OTP sent to your mobile!");
      setStep(2);
    } catch (err) {
      setErrors({ 
        general: (typeof err?.response?.data?.error === 'string' ? err?.response?.data?.error : null) || err?.response?.data?.message || "Could not send OTP. Please try again." 
      });
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    
    if (!validateResetForm()) return;
    
    setLoading(true);
    try {
      // Remove spaces from mobile number before sending to API
      const cleanMobile = mobile.replace(/\s/g, '');
      
      // Your API call here
      await api.post("/forgot-password/", {
        mobile: cleanMobile,
        otp,
        new_password: newPassword,
        confirm_password: newPassword,
      });
      
      // Remove this simulation in production
      console.log("Password reset for:", { mobile: cleanMobile, otp, newPassword });
      alert("Password reset successful! Please log in.");
      navigate("/login");
    } catch (err) {
      setErrors({
        general: (typeof err?.response?.data?.error === 'string' ? err?.response?.data?.error : null) || err?.response?.data?.message || "Reset failed. Please check details and OTP."
      });
    } finally {
      setLoading(false);
    }
  };

  const formatMobileNumber = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{5})(\d{5})/, "$1 $2");
    }
    return numbers.slice(0, 10).replace(/(\d{5})(\d{5})/, "$1 $2");
  };

  const handleMobileChange = (e) => {
    const formatted = formatMobileNumber(e.target.value);
    setMobile(formatted);
    if (errors.mobile) {
      setErrors(prev => ({ ...prev, mobile: "" }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex">
      {/* Left Side - Information Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-green-600 p-12 flex-col justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-white"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 rounded-full bg-white"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 rounded-full bg-white"></div>
        </div>
        
        <div className="relative z-10 text-white">
          <Link to="/" className="flex items-center mb-8 hover:opacity-90 transition-opacity">
            <Heart className="w-10 h-10 mr-3 text-red-300" />
            <h1 className="text-4xl font-bold">Home Cure Express</h1>
          </Link>
          
          <h2 className="text-2xl font-semibold mb-6 leading-tight">
            Secure Password Recovery
          </h2>
          
          <p className="text-lg mb-12 text-blue-100 leading-relaxed">
            Forgot your password? No worries. Enter your mobile number, verify the OTP, 
            and set a new password in seconds.
          </p>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Safe & Secure OTP Verification</h3>
                <p className="text-blue-100">Your security is our top priority</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <KeyRound className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Quick Password Reset</h3>
                <p className="text-blue-100">Reset your password in just 2 simple steps</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <Heart className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">24/7 Customer Support</h3>
                <p className="text-blue-100">We're here to help you anytime</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="lg:hidden mb-8">
            <button onClick={() => navigate('/')} className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors">
              <ArrowLeft className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="text-center">
              <Link to="/" className="inline-flex items-center justify-center hover:opacity-80 transition-opacity">
                <Heart className="w-8 h-8 mr-2 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-800">Home Cure Express</h1>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {/* Progress Indicator */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  1
                </div>
                <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  2
                </div>
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {step === 1 ? 'Reset Your Password' : 'Enter OTP & New Password'}
              </h2>
              <p className="text-gray-600">
                {step === 1 
                  ? 'Enter your mobile number to receive OTP' 
                  : 'Enter the OTP sent to your mobile and create a new password'
                }
              </p>
            </div>

            {errors.general && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errors.general}</p>
              </div>
            )}

            {/* Step 1: Mobile Number */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    <input
                      type="tel"
                      placeholder="Enter mobile number"
                      value={mobile}
                      onChange={handleMobileChange}
                      className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                        errors.mobile
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      maxLength="11"
                    />
                  </div>
                  {errors.mobile && (
                    <p className="mt-2 text-sm text-red-600">{errors.mobile}</p>
                  )}
                </div>

                <button
                  onClick={sendOtp}
                  disabled={loading}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending OTP...</span>
                    </div>
                  ) : (
                    'Send OTP'
                  )}
                </button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="flex items-center justify-center space-x-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Login</span>
                  </Link>
                </div>
              </div>
            )}

            {/* Step 2: OTP and New Password */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    OTP Code
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                        if (errors.otp) setErrors(prev => ({ ...prev, otp: "" }));
                      }}
                      className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                        errors.otp
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      maxLength="6"
                    />
                  </div>
                  {errors.otp && (
                    <p className="mt-2 text-sm text-red-600">{errors.otp}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    <input
                      type="password"
                      placeholder="New password (min 6 chars)"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
                      }}
                      className={`auth-input w-full pl-14 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                        errors.password
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    />
                  </div>
                  {errors.password && (
                    <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                <button
                  onClick={resetPassword}
                  disabled={loading}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Resetting Password...</span>
                    </div>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <div className="text-center space-y-2">
                  <button
                    onClick={() => setStep(1)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    Resend OTP
                  </button>
                  <div>
                    <Link
                      to="/login"
                      className="flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-700 text-sm transition-colors mx-auto"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back to Login</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}